/**
 * The escalation tag every ticket view shows: who must act now (owner request 2026-10-11).
 * Owner (the adventure's creator/owning group), Mods (regional moderators, 7 days open) or
 * System Moderators (site-wide, 14 days open; ownership claims start here).
 */

import { Box, Chip, Tooltip } from '@mui/material'
import { PauseCircleOutline } from '@mui/icons-material'
import { TicketEscalationLabels, TicketEscalationLevel } from '@api/types/tickets'
import { ESCALATION_PAUSED_HINT } from './ticketActions'

const colors: Record<TicketEscalationLevel, 'default' | 'warning' | 'error'> = {
  [TicketEscalationLevel.Owner]: 'default',
  [TicketEscalationLevel.Mods]: 'warning',
  [TicketEscalationLevel.SystemModerators]: 'error',
}

const hints: Record<TicketEscalationLevel, string> = {
  [TicketEscalationLevel.Owner]: "With the adventure's owner",
  [TicketEscalationLevel.Mods]: 'Escalated to the regional moderators (open 7+ days)',
  [TicketEscalationLevel.SystemModerators]: 'Escalated to the site moderators',
}

interface TicketEscalationChipProps {
  level?: TicketEscalationLevel | null
  label?: string | null
  /** Hide the chip while it is still with the owner (dense lists). */
  hideOwner?: boolean
  /** Waiting on the submitter: the escalation clock is stopped. Shows a "Paused" marker beside the tag. */
  paused?: boolean | null
}

export function TicketEscalationChip({ level, label, hideOwner = false, paused = false }: TicketEscalationChipProps) {
  const value = level ?? TicketEscalationLevel.Owner
  if (hideOwner && value === TicketEscalationLevel.Owner && !paused) return null
  const tag = (
    // enterTouchDelay 0: a tap shows the explanation on a phone, where there is no hover.
    <Tooltip title={hints[value] ?? ''} enterTouchDelay={0} leaveTouchDelay={4000}>
      <Chip
        size="small"
        variant={value === TicketEscalationLevel.Owner ? 'outlined' : 'filled'}
        color={colors[value] ?? 'default'}
        label={label || TicketEscalationLabels[value] || 'Owner'}
        sx={{ fontSize: '0.7rem', height: 20 }}
      />
    </Tooltip>
  )
  if (!paused) return tag
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {tag}
      <Tooltip title={ESCALATION_PAUSED_HINT} enterTouchDelay={0} leaveTouchDelay={4000}>
        <Chip
          size="small"
          variant="outlined"
          color="info"
          icon={<PauseCircleOutline />}
          label="Paused"
          sx={{ fontSize: '0.7rem', height: 20 }}
        />
      </Tooltip>
    </Box>
  )
}
